import React from 'react'
import { Box, Button, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import StyledSwitch from '@e3dc-react/shell/Elements/Switch/StyledSwitch'
import { useNavigate, useParams } from 'react-router-dom'
import { J2Editacontent } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { Extension } from 'src/Libs/installation-extensions'
import { Ai360SettingsFormValues } from '../../types'
import { useFormContext, useWatch } from 'react-hook-form'
import { InformationTile } from '../common'
import { TariffsModeModel } from '../../model'
import useUserRoles from 'src/Hooks/user/use-user-roles'
import { useIsTariffRedirectDisabled } from 'src/Libs/system-settings/hooks/use-is-tariff-redirect-disabled'

interface Props {
  isOpen: boolean
  onClose: () => void
  tariffsMode: TariffsModeModel | null
  activeExtension: Extension | undefined
}

const useStyles = makeStyles()(theme => ({
  title: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
    },
  },
  informationTile: {
    marginBlock: theme.spacing(2),
  },
}))

const GridSettingSubPage: React.FC<Props> = ({ isOpen, onClose, tariffsMode, activeExtension }) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()
  const navigate = useNavigate()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  const { control, setValue } = useFormContext<Ai360SettingsFormValues>()
  const isAI360Active = useWatch({ control, name: 'isAI360Active' })
  const isChargingFromGridActive = useWatch({ control, name: 'isChargingFromGridActive' })
  const isSGReadyActive = useWatch({ control, name: 'isSGReadyActive' })
  const { isUser } = useUserRoles()
  const isTariffRedirectDisabled = useIsTariffRedirectDisabled()

  const hasElectricityTariff = tariffsMode?.mode === 'DYNAMIC' || Boolean(activeExtension)
  const tariffRedirectDisabledTooltip = isTariffRedirectDisabled ? t('common.disabled-because-of-offline-system') : undefined

  const handleToggle = (): void => {
    const newValue = !isChargingFromGridActive

    // SG-Ready can work only with activated Grid feature
    if (newValue === false && isSGReadyActive) {
      setValue('isSGReadyActive', false, { shouldDirty: true })
    }

    if (!isAI360Active && newValue) {
      setValue('isAI360Active', true, { shouldDirty: true })
    }

    setValue('isChargingFromGridActive', newValue, { shouldDirty: true })
  }

  const navigateToPriceBasedCharging = (): void => {
    navigate(`/installation/${installationId}/${storageId}/properties/tariffs`)
  }

  const navigateToElectricityTariff = (): void => {
    navigate(`/installation/${installationId}/${storageId}/properties/tariffs`)
  }

  const navigateToExtensions = (): void => {
    navigate(`/installation-extensions/${installationId}/${storageId}`)
  }

  return (
    <Dialog
      fullScreen={!upMd}
      open={isOpen}
      navigateBackButton={true}
      closeButtonVisible={false}
      onClose={onClose}
      onNavigateBack={onClose}
      title={t('ai360.settings-page.grid-settings.title')}
      dialogTitleClassName={classes.title}
      maxWidth="sm"
    >
      <Stack sx={{ pt: 4 }} gap={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h3">{t('ai360.settings-page.grid-settings.charging-from-grid')}</Typography>
          <StyledSwitch checked={isChargingFromGridActive} onChange={handleToggle} />
        </Box>

        <Stack gap={4} mb={2}>
          <Typography variant="body2">{t('ai360.settings-page.grid-settings.charging-from-grid.description')}</Typography>
          {isChargingFromGridActive && <InformationTile message={t('ai360.settings-page.grid-settings.confirm-grid-activation.info')} />}
        </Stack>

        <Typography variant="h3">{t('ai360.settings-page.grid-settings.electricity-tariff')}</Typography>
        {hasElectricityTariff ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {activeExtension ? (
                <Box sx={{ height: 24 }}>{activeExtension.logo}</Box>
              ) : (
                <Typography variant="body2">{t('ai360.settings-page.grid-settings.electricity-tariff.custom-tariff')}</Typography>
              )}
              <Tooltip title={tariffRedirectDisabledTooltip}>
                <span>
                  <J2Editacontent onClick={isTariffRedirectDisabled ? undefined : navigateToElectricityTariff} />
                </span>
              </Tooltip>
            </Box>
            <Tooltip title={tariffRedirectDisabledTooltip}>
              <span>
                <Button onClick={navigateToPriceBasedCharging} variant="text" disabled={isTariffRedirectDisabled}>
                  {t('ai360.settings-page.grid-settings.price-based-charging')}
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <InformationTile
              message={t('ai360.settings-page.grid-settings.inactive-electricity-tariff')}
              containerClassName={classes.informationTile}
            />
            <Box display="flex" justifyContent="space-between">
              <Button variant="text" onClick={navigateToExtensions} disabled={!isUser}>
                {t('ai360.settings-page.grid-settings.tariffs')}
              </Button>
              <Tooltip title={tariffRedirectDisabledTooltip}>
                <span>
                  <Button variant="text" onClick={navigateToElectricityTariff} disabled={isTariffRedirectDisabled}>
                    {t('ai360.settings-page.grid-settings.enter-tariff')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Stack>
    </Dialog>
  )
}

export default GridSettingSubPage
